import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  engineers,
  allSkills,
  allSeniorities,
  allTimezones,
  allStatuses,
  type Engineer,
} from '@/mocks/talentPool';
import RequestMatchModal from './RequestMatchModal';

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

const statusConfig: Record<string, { dot: string; bg: string; text: string; border: string }> = {
  'Available Now': {
    dot: 'bg-emerald-500',
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-200',
  },
  Interviewing: {
    dot: 'bg-amber-500',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200',
  },
  'Matched This Week': {
    dot: 'bg-gray-400',
    bg: 'bg-gray-50',
    text: 'text-gray-600',
    border: 'border-gray-200',
  },
  'Returning Soon': {
    dot: 'bg-sky-500',
    bg: 'bg-sky-50',
    text: 'text-sky-700',
    border: 'border-sky-200',
  },
};

const seniorityOrder: Record<string, number> = {
  Junior: 1,
  Mid: 2,
  Senior: 3,
  Lead: 4,
  Principal: 5,
};

export default function TalentPoolPreview() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.08 });

  const [search, setSearch] = useState('');
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [selectedSeniorities, setSelectedSeniorities] = useState<string[]>([]);
  const [selectedTimezones, setSelectedTimezones] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(['Available Now']);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEngineer, setSelectedEngineer] = useState<Engineer | null>(null);

  const openModal = (engineer: Engineer) => {
    setSelectedEngineer(engineer);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    // small delay to allow exit animation before clearing engineer
    setTimeout(() => setSelectedEngineer(null), 300);
  };

  const toggleSkill = (skill: string) => {
    setSelectedSkills((prev) =>
      prev.includes(skill) ? prev.filter((s) => s !== skill) : [...prev, skill]
    );
  };

  const toggleSeniority = (sen: string) => {
    setSelectedSeniorities((prev) =>
      prev.includes(sen) ? prev.filter((s) => s !== sen) : [...prev, sen]
    );
  };

  const toggleTimezone = (tz: string) => {
    setSelectedTimezones((prev) =>
      prev.includes(tz) ? prev.filter((t) => t !== tz) : [...prev, tz]
    );
  };

  const toggleStatus = (st: string) => {
    setSelectedStatuses((prev) =>
      prev.includes(st) ? prev.filter((s) => s !== st) : [...prev, st]
    );
  };

  const clearFilters = () => {
    setSearch('');
    setSelectedSkills([]);
    setSelectedSeniorities([]);
    setSelectedTimezones([]);
    setSelectedStatuses([]);
  };

  const filtered = useMemo(() => {
    return engineers.filter((e) => {
      const matchesSearch =
        !search ||
        e.name.toLowerCase().includes(search.toLowerCase()) ||
        e.role.toLowerCase().includes(search.toLowerCase()) ||
        e.skills.some((s) => s.toLowerCase().includes(search.toLowerCase()));

      const matchesSkill =
        selectedSkills.length === 0 || e.skills.some((s) => selectedSkills.includes(s));

      const matchesSeniority =
        selectedSeniorities.length === 0 || selectedSeniorities.includes(e.seniority);

      const matchesTimezone =
        selectedTimezones.length === 0 || selectedTimezones.includes(e.timezone);

      const matchesStatus =
        selectedStatuses.length === 0 || selectedStatuses.includes(e.status);

      return matchesSearch && matchesSkill && matchesSeniority && matchesTimezone && matchesStatus;
    });
  }, [search, selectedSkills, selectedSeniorities, selectedTimezones, selectedStatuses]);

  const sorted = useMemo(() => {
    return [...filtered].sort(
      (a, b) => seniorityOrder[b.seniority] - seniorityOrder[a.seniority]
    );
  }, [filtered]);

  const activeFilterCount =
    selectedSkills.length +
    selectedSeniorities.length +
    selectedTimezones.length +
    (selectedStatuses.length === allStatuses.length ? 0 : selectedStatuses.length);

  const availableCount = engineers.filter((e) => e.status === 'Available Now').length;
  const interviewingCount = engineers.filter((e) => e.status === 'Interviewing').length;

  return (
    <section
      id="talent-pool"
      className="py-16 md:py-24 bg-gray-50/50 relative overflow-hidden"
      ref={ref}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-10 md:mb-14"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-user-search-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Browse Our Talent</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Talent Pool
            <br />
            <span className="text-primary-600">Preview</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            Filter by skill, seniority, timezone, and availability. Every profile is pre-vetted, interview-ready, and matched to your stack.
          </motion.p>
        </motion.div>

        {/* Quick Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8 md:mb-10"
        >
          {[
            {
              label: 'Engineers in Pool',
              value: engineers.length,
              icon: 'ri-team-line',
              color: 'text-navy-900',
            },
            {
              label: 'Available Now',
              value: availableCount,
              icon: 'ri-user-add-line',
              color: 'text-emerald-600',
            },
            {
              label: 'In Interviews',
              value: interviewingCount,
              icon: 'ri-video-chat-line',
              color: 'text-amber-600',
            },
            {
              label: 'Countries Covered',
              value: new Set(engineers.map((e) => e.timezone.split('(')[1]?.replace(')', ''))).size,
              icon: 'ri-global-line',
              color: 'text-primary-600',
            },
          ].map((stat, i) => (
            <div
              key={i}
              className="bg-white border border-gray-100 rounded-xl p-3 md:p-4 flex items-center gap-3"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-gray-50 flex-shrink-0">
                <i className={`${stat.icon} ${stat.color} text-lg`}></i>
              </div>
              <div>
                <p className="text-lg md:text-xl font-bold text-navy-900">{stat.value}</p>
                <p className="text-xs text-gray-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.35, duration: 0.5 }}
          className="bg-white border border-gray-100 rounded-2xl p-4 md:p-6 mb-6 md:mb-8"
        >
          {/* Search + Clear */}
          <div className="flex flex-col sm:flex-row gap-3 mb-5">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-base"></i>
              <input
                type="text"
                placeholder="Search by name, role, or skill..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm text-navy-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 transition-all"
              />
            </div>
            {activeFilterCount > 0 && (
              <button
                onClick={clearFilters}
                className="px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 hover:text-navy-900 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-close-circle-line"></i>
                Clear {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''}
              </button>
            )}
          </div>

          {/* Skill Filters */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Skills</span>
              {selectedSkills.length > 0 && (
                <span className="text-xs text-primary-600 font-medium">{selectedSkills.length} selected</span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {allSkills.map((skill) => {
                const isActive = selectedSkills.includes(skill);
                return (
                  <button
                    key={skill}
                    onClick={() => toggleSkill(skill)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                      isActive
                        ? 'bg-primary-500 text-white border-primary-500 shadow-sm'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-primary-300 hover:text-primary-600'
                    }`}
                  >
                    {skill}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Seniority + Status + Timezone */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {/* Seniority */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Seniority</span>
                {selectedSeniorities.length > 0 && (
                  <span className="text-xs text-primary-600 font-medium">{selectedSeniorities.length} selected</span>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {allSeniorities.map((sen) => {
                  const isActive = selectedSeniorities.includes(sen);
                  return (
                    <button
                      key={sen}
                      onClick={() => toggleSeniority(sen)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                        isActive
                          ? 'bg-navy-900 text-white border-navy-900'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-navy-700 hover:text-navy-800'
                      }`}
                    >
                      {sen}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Status */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Status</span>
                {selectedStatuses.length > 0 && (
                  <span className="text-xs text-primary-600 font-medium">{selectedStatuses.length} selected</span>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {allStatuses.map((st) => {
                  const isActive = selectedStatuses.includes(st);
                  return (
                    <button
                      key={st}
                      onClick={() => toggleStatus(st)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                        isActive
                          ? 'bg-emerald-500 text-white border-emerald-500'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-emerald-400 hover:text-emerald-600'
                      }`}
                    >
                      {st}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Timezone */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Timezone</span>
                {selectedTimezones.length > 0 && (
                  <span className="text-xs text-primary-600 font-medium">{selectedTimezones.length} selected</span>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {allTimezones.map((tz) => {
                  const isActive = selectedTimezones.includes(tz);
                  return (
                    <button
                      key={tz}
                      onClick={() => toggleTimezone(tz)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                        isActive
                          ? 'bg-sky-500 text-white border-sky-500'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-sky-400 hover:text-sky-600'
                      }`}
                    >
                      {tz.split('(')[1]?.replace(')', '') || tz}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Results Count */}
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <p className="text-sm text-gray-500">
            Showing <span className="font-semibold text-navy-900">{sorted.length}</span> engineer{sorted.length !== 1 ? 's' : ''}
            {activeFilterCount > 0 && (
              <span> matching your filters</span>
            )}
          </p>
          <div className="hidden md:flex items-center gap-3 text-xs text-gray-400">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
              Available
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-amber-500"></span>
              Interviewing
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-gray-400"></span>
              Matched
            </span>
          </div>
        </div>

        {/* Engineer Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          <AnimatePresence mode="popLayout">
            {sorted.map((engineer, index) => (
              <motion.div
                key={engineer.id}
                layout
                initial={{ opacity: 0, scale: 0.92, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.92, y: -10 }}
                transition={{ duration: 0.3, delay: Math.min(index * 0.04, 0.3) }}
                className="bg-white border border-gray-100 rounded-2xl p-5 md:p-6 hover:border-primary-200 hover:shadow-lg hover:shadow-primary-500/5 transition-all duration-300 group"
              >
                {/* Header: Avatar + Status */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-11 h-11 md:w-12 md:h-12 rounded-xl ${engineer.avatarColor} flex items-center justify-center flex-shrink-0`}
                    >
                      <span className="text-white text-sm md:text-base font-bold">
                        {engineer.initials}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-sm md:text-base font-bold text-navy-900 leading-tight">
                        {engineer.name}
                      </h3>
                      <p className="text-xs text-gray-500">{engineer.role}</p>
                    </div>
                  </div>
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] md:text-xs font-semibold border ${
                      statusConfig[engineer.status].bg
                    } ${statusConfig[engineer.status].text} ${statusConfig[engineer.status].border}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${statusConfig[engineer.status].dot}`}></span>
                    {engineer.status}
                  </span>
                </div>

                {/* Skills */}
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {engineer.skills.slice(0, 4).map((skill) => (
                    <span
                      key={skill}
                      className="px-2 py-0.5 bg-gray-50 border border-gray-100 rounded text-[10px] md:text-xs font-medium text-gray-600"
                    >
                      {skill}
                    </span>
                  ))}
                  {engineer.skills.length > 4 && (
                    <span className="px-2 py-0.5 bg-gray-50 border border-gray-100 rounded text-[10px] md:text-xs font-medium text-gray-400">
                      +{engineer.skills.length - 4}
                    </span>
                  )}
                </div>

                {/* Meta Row */}
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="bg-gray-50 rounded-lg p-2 text-center">
                    <p className="text-xs font-bold text-navy-900">{engineer.years}yr</p>
                    <p className="text-[10px] text-gray-500">Experience</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2 text-center">
                    <p className="text-xs font-bold text-navy-900">{engineer.seniority}</p>
                    <p className="text-[10px] text-gray-500">Level</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2 text-center">
                    <p className="text-xs font-bold text-navy-900">{engineer.timezoneOffset}</p>
                    <p className="text-[10px] text-gray-500">{engineer.timezone.split('(')[1]?.replace(')', '')}</p>
                  </div>
                </div>

                {/* Languages */}
                <div className="flex items-center gap-1.5 mb-4 flex-wrap">
                  <i className="ri-translate-2 text-gray-400 text-xs"></i>
                  {engineer.languages.map((lang, i) => (
                    <span key={lang} className="text-[10px] md:text-xs text-gray-500">
                      {lang}
                      {i < engineer.languages.length - 1 && <span className="text-gray-300 mx-1">·</span>}
                    </span>
                  ))}
                </div>

                {/* Highlight */}
                {engineer.highlight && (
                  <div className="flex items-start gap-2 mb-4 bg-primary-50/50 border border-primary-100 rounded-lg p-2.5">
                    <i className="ri-star-smile-line text-primary-500 text-xs mt-0.5 flex-shrink-0"></i>
                    <p className="text-[10px] md:text-xs text-primary-700 leading-relaxed">
                      {engineer.highlight}
                    </p>
                  </div>
                )}

                {/* Rate + CTA */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <div>
                    <p className="text-xs text-gray-400">Rate range</p>
                    <p className="text-sm md:text-base font-bold text-navy-900">
                      ${engineer.rateMin}–${engineer.rateMax}
                      <span className="text-xs font-normal text-gray-400">/hr</span>
                    </p>
                  </div>
                  <button
                    onClick={() => openModal(engineer)}
                    className="px-4 py-2 bg-primary-500 text-white text-xs md:text-sm font-semibold rounded-lg hover:shadow-md hover:shadow-primary-500/20 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer whitespace-nowrap group/btn"
                  >
                    Request Match
                    <i className="ri-arrow-right-line ml-1 group-hover/btn:translate-x-0.5 transition-transform inline-block"></i>
                  </button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Empty State */}
        {sorted.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16 md:py-20"
          >
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-gray-100 rounded-full">
              <i className="ri-search-2-line text-2xl text-gray-400"></i>
            </div>
            <h3 className="text-lg font-bold text-navy-900 mb-2">No engineers match your filters</h3>
            <p className="text-sm text-gray-500 mb-6">
              Try removing some filters or adjusting your search. We source new talent weekly.
            </p>
            <button
              onClick={clearFilters}
              className="px-6 py-2.5 bg-primary-500 text-white text-sm font-semibold rounded-lg hover:shadow-md transition-all cursor-pointer"
            >
              Reset All Filters
            </button>
          </motion.div>
        )}

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.5, ...springTransition }}
          className="mt-10 md:mt-14 text-center"
        >
          <p className="text-sm text-gray-500 mb-4">
            This is a live preview of our current pool. New engineers are added every week.
          </p>
          <button
            onClick={() => {
              const el = document.getElementById('cta');
              if (el) {
                const offset = 80;
                const pos = el.getBoundingClientRect().top + window.pageYOffset - offset;
                window.scrollTo({ top: pos, behavior: 'smooth' });
              }
            }}
            className="px-8 py-3.5 bg-navy-900 text-white text-base font-semibold rounded-xl hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 cursor-pointer whitespace-nowrap group"
          >
            Get Matched with an Engineer
            <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform inline-block"></i>
          </button>
        </motion.div>

        {/* Request Match Modal */}
        <RequestMatchModal
          engineer={selectedEngineer}
          isOpen={modalOpen}
          onClose={closeModal}
        />
      </div>
    </section>
  );
}